'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';


// ─── Types ────────────────────────────────────────────────────────────────────

type ItemType = 'trip' | 'hotel' | 'guide' | 'destination';

interface CollectionItem {
  id: string;
  type: ItemType;
  title: string;
  subtitle?: string;
  image?: string;
  price?: number;
  currency?: string;
  rating?: number;
  location?: string;
  tags?: string[];
  href?: string;
  savedAt: string;
  collectionId: string;
}

interface Collection {
  id: string;
  name: string;
  emoji: string;
  color: string;
  description?: string;
  createdAt: string;
  itemCount: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'yn_collections';
const ITEMS_KEY = 'yn_collection_items';

const TYPE_META: Record<ItemType, { label: string; emoji: string; color: string }> = {
  trip:        { label: 'Trip',        emoji: '🏔️', color: '#F4B400' },
  hotel:       { label: 'Hotel',       emoji: '🏨', color: '#60a5fa' },
  guide:       { label: 'Guide',       emoji: '🧑‍💼', color: '#34d399' },
  destination: { label: 'Destination', emoji: '📍', color: '#f472b6' },
};

const PALETTE = [
  { color: '#F4B400', bg: 'rgba(244,180,0,0.12)'  },
  { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  { color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  { color: '#f472b6', bg: 'rgba(244,114,182,0.12)'},
  { color: '#a78bfa', bg: 'rgba(167,139,250,0.12)'},
  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
];

const EMOJIS = ['📁','⭐','❤️','🏔️','🌏','🎒','🗺️','🏖️','🌿','💎','🔥','✈️'];

const DEFAULT_COLLECTION: Collection = {
  id: 'default',
  name: 'My Favourites',
  emoji: '❤️',
  color: '#F4B400',
  description: 'Your default collection',
  createdAt: new Date().toISOString(),
  itemCount: 0,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function loadCollections(): Collection[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [DEFAULT_COLLECTION];
    const parsed = JSON.parse(raw) as Collection[];
    return parsed.length ? parsed : [DEFAULT_COLLECTION];
  } catch { return [DEFAULT_COLLECTION]; }
}

function loadItems(): CollectionItem[] {
  try {
    const raw = localStorage.getItem(ITEMS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveCollections(cols: Collection[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cols)); } catch {}
}

function saveItems(items: CollectionItem[]) {
  try { localStorage.setItem(ITEMS_KEY, JSON.stringify(items)); } catch {}
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="text-7xl mb-5 opacity-60">🗂️</div>
      <h3 className="text-snow font-bold text-xl mb-2">Nothing saved yet</h3>
      <p className="text-snow/40 text-sm max-w-xs mb-6">
        Save trips, hotels, guides and destinations to your collections for quick re-access and comparison.
      </p>
      <button onClick={onAdd} className="btn-gold px-6 py-2.5 rounded-xl text-sm font-semibold">
        + Create Collection
      </button>
    </div>
  );
}

function CollectionCard({
  col,
  itemCount,
  active,
  onClick,
  onEdit,
  onDelete,
}: {
  col: Collection;
  itemCount: number;
  active: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      onClick={onClick}
      className="relative cursor-pointer rounded-2xl p-4 transition-all duration-200 group"
      style={{
        background: active ? `rgba(${hexToRgb(col.color)},0.14)` : 'rgba(255,255,255,0.04)',
        border: `1px solid ${active ? col.color + '55' : 'rgba(255,255,255,0.07)'}`,
        boxShadow: active ? `0 0 0 1px ${col.color}33` : undefined,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-2xl shrink-0">{col.emoji}</span>
          <div className="min-w-0">
            <div className="font-semibold text-sm text-snow truncate">{col.name}</div>
            <div className="text-xs text-snow/40 mt-0.5">{itemCount} item{itemCount !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className="relative shrink-0" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="w-7 h-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/10"
            style={{ color: 'rgba(245,240,232,0.5)' }}
          >
            ⋯
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-36 rounded-xl shadow-2xl overflow-hidden z-20"
              style={{ background: '#1A3870', border: '1px solid rgba(244,180,0,0.12)' }}
            >
              <button onClick={() => { onEdit(); setMenuOpen(false); }} className="w-full text-left px-3 py-2.5 text-xs hover:bg-white/5 text-snow/70">✏️ Rename</button>
              {col.id !== 'default' && (
                <button onClick={() => { onDelete(); setMenuOpen(false); }} className="w-full text-left px-3 py-2.5 text-xs hover:bg-white/5 text-red-400/80">🗑️ Delete</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemCard({
  item,
  selected,
  onSelect,
  onRemove,
  compareMode,
}: {
  item: CollectionItem;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  compareMode: boolean;
}) {
  const meta = TYPE_META[item.type];
  return (
    <div
      className="yn-card rounded-2xl overflow-hidden group relative transition-all duration-200"
      style={{
        border: selected ? `1.5px solid ${meta.color}` : '1px solid rgba(255,255,255,0.06)',
        boxShadow: selected ? `0 0 0 2px ${meta.color}22` : undefined,
      }}
    >
      {/* Compare checkbox */}
      {compareMode && (
        <button
          onClick={onSelect}
          className="absolute top-3 left-3 z-10 w-6 h-6 rounded-md flex items-center justify-center transition-all"
          style={{
            background: selected ? meta.color : 'rgba(11,45,92,0.7)',
            border: `1.5px solid ${selected ? meta.color : 'rgba(255,255,255,0.3)'}`,
            backdropFilter: 'blur(8px)',
          }}
        >
          {selected && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0B2D5C" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
        </button>
      )}

      {/* Image */}
      <div className="relative h-40 bg-navy-mid overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl opacity-20">{meta.emoji}</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />

        {/* Type badge */}
        <div
          className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: meta.color + '22', color: meta.color, border: `1px solid ${meta.color}44`, backdropFilter: 'blur(8px)' }}
        >
          {meta.emoji} {meta.label}
        </div>

        {/* Remove button */}
        <button
          onClick={onRemove}
          className="absolute bottom-3 right-3 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: 'rgba(239,68,68,0.7)', backdropFilter: 'blur(8px)' }}
          title="Remove from collection"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-3.5">
        <h3 className="text-snow font-semibold text-sm line-clamp-1 mb-0.5">{item.title}</h3>
        {item.subtitle && <p className="text-snow/50 text-xs line-clamp-1 mb-2">{item.subtitle}</p>}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-snow/40">
            {item.location && <span>📍 {item.location}</span>}
            {item.rating && <span>⭐ {item.rating.toFixed(1)}</span>}
          </div>
          {item.price !== undefined && (
            <span className="text-xs font-bold" style={{ color: 'var(--gold-bright)' }}>
              {item.currency || 'NPR'} {item.price.toLocaleString()}
            </span>
          )}
        </div>

        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(244,180,0,0.08)', color: 'rgba(244,180,0,0.7)' }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {item.href && (
          <Link href={item.href} className="block mt-3 text-center text-xs font-medium py-1.5 rounded-lg transition-colors hover:bg-gold/10" style={{ color: 'var(--gold-bright)', border: '1px solid rgba(244,180,0,0.2)' }}>
            View Details →
          </Link>
        )}
      </div>
    </div>
  );
}

function ComparePanel({ items, onClose }: { items: CollectionItem[]; onClose: () => void }) {
  if (items.length < 2) return null;
  const fields: { key: keyof CollectionItem; label: string }[] = [
    { key: 'type',     label: 'Type'     },
    { key: 'location', label: 'Location' },
    { key: 'rating',   label: 'Rating'   },
    { key: 'price',    label: 'Price'    },
  ];

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 shadow-2xl"
      style={{ background: 'rgba(11,29,60,0.98)', backdropFilter: 'blur(24px)', borderTop: '1px solid rgba(244,180,0,0.15)' }}
    >
      <div className="max-w-5xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-snow font-bold text-sm">Comparing {items.length} items</h3>
          <button onClick={onClose} className="text-snow/50 hover:text-snow text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <td className="text-snow/40 pr-4 py-1.5 font-medium w-24">Field</td>
                {items.map(item => (
                  <td key={item.id} className="text-snow font-semibold px-3 py-1.5 text-center min-w-[140px]">
                    <div className="line-clamp-1">{item.title}</div>
                    <div className="text-[10px] text-snow/40 font-normal mt-0.5">{TYPE_META[item.type].emoji} {TYPE_META[item.type].label}</div>
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(f => (
                <tr key={f.key} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <td className="text-snow/40 pr-4 py-2 font-medium">{f.label}</td>
                  {items.map(item => {
                    const val = item[f.key];
                    let display = '—';
                    if (val !== undefined && val !== null && val !== '') {
                      if (f.key === 'type') display = `${TYPE_META[item.type].emoji} ${TYPE_META[item.type].label}`;
                      else if (f.key === 'price') display = `${item.currency || 'NPR'} ${Number(val).toLocaleString()}`;
                      else if (f.key === 'rating') display = `⭐ ${Number(val).toFixed(1)}`;
                      else display = String(val);
                    }
                    return (
                      <td key={item.id} className="px-3 py-2 text-center text-snow/70">{display}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function CollectionModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: Partial<Collection>;
  onSave: (data: { name: string; emoji: string; color: string; description: string }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [emoji, setEmoji] = useState(initial?.emoji || '📁');
  const [color, setColor] = useState(initial?.color || '#F4B400');
  const [desc, setDesc] = useState(initial?.description || '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: 'linear-gradient(160deg,#1A3870,#0B2D5C)', border: '1px solid rgba(244,180,0,0.15)' }}>
        <h2 className="text-snow font-bold text-lg mb-5">{initial?.id ? 'Edit Collection' : 'New Collection'}</h2>

        {/* Emoji picker */}
        <div className="mb-4">
          <label className="text-snow/50 text-xs mb-2 block">Icon</label>
          <div className="flex flex-wrap gap-2">
            {EMOJIS.map(e => (
              <button
                key={e}
                onClick={() => setEmoji(e)}
                className="w-9 h-9 rounded-xl text-lg flex items-center justify-center transition-all"
                style={{ background: emoji === e ? 'rgba(244,180,0,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${emoji === e ? 'rgba(244,180,0,0.5)' : 'rgba(255,255,255,0.08)'}` }}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        {/* Color picker */}
        <div className="mb-4">
          <label className="text-snow/50 text-xs mb-2 block">Color</label>
          <div className="flex gap-2">
            {PALETTE.map(p => (
              <button
                key={p.color}
                onClick={() => setColor(p.color)}
                className="w-8 h-8 rounded-full transition-transform hover:scale-110"
                style={{ background: p.color, boxShadow: color === p.color ? `0 0 0 2px #0B2D5C, 0 0 0 4px ${p.color}` : undefined }}
              />
            ))}
          </div>
        </div>

        {/* Name */}
        <div className="mb-4">
          <label className="text-snow/50 text-xs mb-1.5 block">Collection Name *</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Himalayan Treks"
            maxLength={40}
            className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20"
          />
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="text-snow/50 text-xs mb-1.5 block">Description (optional)</label>
          <input
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder="A short note about this collection"
            maxLength={80}
            className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20"
          />
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-snow/60 border border-white/10 hover:bg-white/5 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => name.trim() && onSave({ name: name.trim(), emoji, color, description: desc })}
            disabled={!name.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg,#F4B400,#E0AA18)', color: '#0B2D5C' }}
          >
            {initial?.id ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Item Modal ───────────────────────────────────────────────────────────

function AddItemModal({
  collections,
  onSave,
  onClose,
}: {
  collections: Collection[];
  onSave: (item: Omit<CollectionItem, 'id' | 'savedAt'>) => void;
  onClose: () => void;
}) {
  const [type, setType] = useState<ItemType>('trip');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [rating, setRating] = useState('');
  const [tags, setTags] = useState('');
  const [href, setHref] = useState('');
  const [collectionId, setCollectionId] = useState(collections[0]?.id || 'default');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: 'linear-gradient(160deg,#1A3870,#0B2D5C)', border: '1px solid rgba(244,180,0,0.15)' }}>
        <h2 className="text-snow font-bold text-lg mb-5">Save Item</h2>

        {/* Type */}
        <div className="mb-4">
          <label className="text-snow/50 text-xs mb-2 block">Type</label>
          <div className="grid grid-cols-4 gap-2">
            {(Object.keys(TYPE_META) as ItemType[]).map(t => (
              <button
                key={t}
                onClick={() => setType(t)}
                className="py-2 rounded-xl text-xs font-medium transition-all"
                style={{
                  background: type === t ? TYPE_META[t].color + '22' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${type === t ? TYPE_META[t].color + '55' : 'rgba(255,255,255,0.08)'}`,
                  color: type === t ? TYPE_META[t].color : 'rgba(245,240,232,0.5)',
                }}
              >
                {TYPE_META[t].emoji}<br />{TYPE_META[t].label}
              </button>
            ))}
          </div>
        </div>

        {/* Collection */}
        <div className="mb-4">
          <label className="text-snow/50 text-xs mb-1.5 block">Add to Collection</label>
          <select
            value={collectionId}
            onChange={e => setCollectionId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40"
            style={{ background: 'rgba(11,45,92,0.8)' }}
          >
            {collections.map(c => (
              <option key={c.id} value={c.id} style={{ background: '#0B2D5C' }}>{c.emoji} {c.name}</option>
            ))}
          </select>
        </div>

        {/* Title */}
        <div className="mb-3">
          <label className="text-snow/50 text-xs mb-1.5 block">Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Everest Base Camp Trek" className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20" />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-snow/50 text-xs mb-1.5 block">Subtitle</label>
            <input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Short description" className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20" />
          </div>
          <div>
            <label className="text-snow/50 text-xs mb-1.5 block">Location</label>
            <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Solukhumbu" className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-snow/50 text-xs mb-1.5 block">Price (NPR)</label>
            <input value={price} onChange={e => setPrice(e.target.value)} type="number" placeholder="e.g. 45000" className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20" />
          </div>
          <div>
            <label className="text-snow/50 text-xs mb-1.5 block">Rating (0–5)</label>
            <input value={rating} onChange={e => setRating(e.target.value)} type="number" min="0" max="5" step="0.1" placeholder="e.g. 4.8" className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20" />
          </div>
        </div>

        <div className="mb-3">
          <label className="text-snow/50 text-xs mb-1.5 block">Tags (comma-separated)</label>
          <input value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. trekking, high altitude, scenic" className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20" />
        </div>

        <div className="mb-6">
          <label className="text-snow/50 text-xs mb-1.5 block">Link (optional)</label>
          <input value={href} onChange={e => setHref(e.target.value)} placeholder="e.g. /packages/ebc-trek" className="w-full px-3 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/40 placeholder-snow/20" />
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-snow/60 border border-white/10 hover:bg-white/5 transition-colors">Cancel</button>
          <button
            onClick={() => {
              if (!title.trim()) return;
              onSave({
                type,
                title: title.trim(),
                subtitle: subtitle.trim() || undefined,
                location: location.trim() || undefined,
                price: price ? Number(price) : undefined,
                rating: rating ? Number(rating) : undefined,
                tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
                href: href.trim() || undefined,
                collectionId,
              });
            }}
            disabled={!title.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg,#F4B400,#E0AA18)', color: '#0B2D5C' }}
          >
            Save Item
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '244,180,0';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CollectionsPage() {
  const { user } = useAuth();

  const [collections, setCollections] = useState<Collection[]>([]);
  const [items, setItems] = useState<CollectionItem[]>([]);
  const [activeColId, setActiveColId] = useState<string>('default');
  const [filterType, setFilterType] = useState<ItemType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showColModal, setShowColModal] = useState(false);
  const [editingCol, setEditingCol] = useState<Collection | null>(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const cols = loadCollections();
    const its = loadItems();
    setCollections(cols);
    setItems(its);
    setActiveColId(cols[0]?.id || 'default');
  }, []);

  const persistCollections = useCallback((cols: Collection[]) => {
    setCollections(cols);
    saveCollections(cols);
  }, []);

  const persistItems = useCallback((its: CollectionItem[]) => {
    setItems(its);
    saveItems(its);
  }, []);

  // Derived
  const activeItems = items.filter(i => i.collectionId === activeColId);
  const filteredItems = activeItems.filter(i => {
    if (filterType !== 'all' && i.type !== filterType) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return i.title.toLowerCase().includes(q) || i.location?.toLowerCase().includes(q) || i.tags?.some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  const compareItems = items.filter(i => selectedIds.includes(i.id));

  // Collection CRUD
  const createCollection = (data: { name: string; emoji: string; color: string; description: string }) => {
    const newCol: Collection = {
      id: `col_${Date.now()}`,
      name: data.name,
      emoji: data.emoji,
      color: data.color,
      description: data.description,
      createdAt: new Date().toISOString(),
      itemCount: 0,
    };
    persistCollections([...collections, newCol]);
    setActiveColId(newCol.id);
    setShowColModal(false);
  };

  const updateCollection = (data: { name: string; emoji: string; color: string; description: string }) => {
    if (!editingCol) return;
    persistCollections(collections.map(c => c.id === editingCol.id ? { ...c, ...data } : c));
    setEditingCol(null);
  };

  const deleteCollection = (colId: string) => {
    if (colId === 'default') return;
    const updated = collections.filter(c => c.id !== colId);
    persistCollections(updated);
    persistItems(items.filter(i => i.collectionId !== colId));
    setActiveColId(updated[0]?.id || 'default');
  };

  // Item CRUD
  const addItem = (data: Omit<CollectionItem, 'id' | 'savedAt'>) => {
    const newItem: CollectionItem = {
      ...data,
      id: `item_${Date.now()}`,
      savedAt: new Date().toISOString(),
    };
    persistItems([...items, newItem]);
    setShowAddItem(false);
  };

  const removeItem = (itemId: string) => {
    persistItems(items.filter(i => i.id !== itemId));
    setSelectedIds(prev => prev.filter(id => id !== itemId));
  };

  const toggleSelect = (itemId: string) => {
    setSelectedIds(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : prev.length < 4 ? [...prev, itemId] : prev
    );
  };

  const activeCol = collections.find(c => c.id === activeColId);

  if (!mounted) {
    return (
      <main className="min-h-screen bg-navy">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-navy">
      <Header />

      <div className="pt-20 max-w-7xl mx-auto px-4 lg:px-6 py-8">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-snow font-bold text-2xl lg:text-3xl">🗂️ My Collections</h1>
            <p className="text-snow/40 text-sm mt-1">
              {items.length} saved item{items.length !== 1 ? 's' : ''} across {collections.length} collection{collections.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setCompareMode(v => !v); setSelectedIds([]); }}
              className="text-xs font-medium px-4 py-2 rounded-xl transition-all"
              style={{
                background: compareMode ? 'rgba(244,180,0,0.15)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${compareMode ? 'rgba(244,180,0,0.4)' : 'rgba(255,255,255,0.1)'}`,
                color: compareMode ? 'var(--gold-bright)' : 'rgba(245,240,232,0.6)',
              }}
            >
              {compareMode ? '✓ Compare Mode' : '⚖️ Compare'}
            </button>
            <button
              onClick={() => setShowAddItem(true)}
              className="text-xs font-semibold px-4 py-2 rounded-xl transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg,#F4B400,#E0AA18)', color: '#0B2D5C' }}
            >
              + Save Item
            </button>
          </div>
        </div>

        {compareMode && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs" style={{ background: 'rgba(244,180,0,0.08)', border: '1px solid rgba(244,180,0,0.2)', color: 'rgba(244,180,0,0.8)' }}>
            ⚖️ Select 2–4 items to compare side by side. {selectedIds.length > 0 && `${selectedIds.length} selected.`}
            {selectedIds.length >= 2 && (
              <button
                onClick={() => {}}
                className="ml-3 underline font-semibold"
              >
                View comparison ↓
              </button>
            )}
          </div>
        )}

        <div className="flex gap-6 lg:gap-8">
          {/* Sidebar — Collections */}
          <aside className="hidden md:block w-56 lg:w-64 shrink-0">
            <div className="flex items-center justify-between mb-3">
              <span className="text-snow/50 text-xs font-semibold uppercase tracking-wider">Collections</span>
              <button
                onClick={() => setShowColModal(true)}
                className="w-6 h-6 rounded-lg flex items-center justify-center text-snow/40 hover:text-gold hover:bg-gold/10 transition-colors text-lg leading-none"
                title="New collection"
              >
                +
              </button>
            </div>
            <div className="space-y-2">
              {collections.map(col => (
                <CollectionCard
                  key={col.id}
                  col={col}
                  itemCount={items.filter(i => i.collectionId === col.id).length}
                  active={activeColId === col.id}
                  onClick={() => setActiveColId(col.id)}
                  onEdit={() => setEditingCol(col)}
                  onDelete={() => deleteCollection(col.id)}
                />
              ))}
            </div>

            {/* Quick links */}
            <div className="mt-6 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="text-snow/30 text-[10px] font-semibold uppercase tracking-wider mb-3">Quick Access</div>
              {[
                { label: '🏔️ Packages', href: '/packages' },
                { label: '🏨 Hotels', href: '/hotels' },
                { label: '🧑‍💼 Guides', href: '/guides' },
                { label: '📍 Destinations', href: '/destinations' },
                { label: '❤️ Wishlist', href: '/wishlist' },
              ].map(l => (
                <Link key={l.href} href={l.href} className="block text-xs py-2 text-snow/40 hover:text-snow/70 transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Mobile collection tabs */}
            <div className="md:hidden flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
              {collections.map(col => (
                <button
                  key={col.id}
                  onClick={() => setActiveColId(col.id)}
                  className="shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: activeColId === col.id ? col.color + '22' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${activeColId === col.id ? col.color + '55' : 'rgba(255,255,255,0.08)'}`,
                    color: activeColId === col.id ? col.color : 'rgba(245,240,232,0.5)',
                  }}
                >
                  {col.emoji} {col.name}
                </button>
              ))}
              <button
                onClick={() => setShowColModal(true)}
                className="shrink-0 px-3 py-1.5 rounded-full text-xs font-medium text-snow/30 border border-white/08 hover:bg-white/5 transition-colors"
                style={{ border: '1px solid rgba(255,255,255,0.08)' }}
              >
                + New
              </button>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              {/* Search */}
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
                </svg>
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search saved items…"
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-snow bg-white/5 border border-white/10 focus:outline-none focus:border-gold/30 placeholder-snow/20"
                />
              </div>

              {/* Type filter */}
              <div className="flex gap-1.5">
                {(['all', ...Object.keys(TYPE_META)] as (ItemType | 'all')[]).map(t => (
                  <button
                    key={t}
                    onClick={() => setFilterType(t)}
                    className="px-3 py-2 rounded-xl text-xs font-medium transition-all"
                    style={{
                      background: filterType === t ? (t === 'all' ? 'rgba(244,180,0,0.15)' : TYPE_META[t as ItemType].color + '22') : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${filterType === t ? (t === 'all' ? 'rgba(244,180,0,0.4)' : TYPE_META[t as ItemType].color + '55') : 'rgba(255,255,255,0.08)'}`,
                      color: filterType === t ? (t === 'all' ? 'var(--gold-bright)' : TYPE_META[t as ItemType].color) : 'rgba(245,240,232,0.4)',
                    }}
                  >
                    {t === 'all' ? 'All' : `${TYPE_META[t as ItemType].emoji} ${TYPE_META[t as ItemType].label}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Collection header */}
            {activeCol && (
              <div className="flex items-center gap-3 mb-5">
                <span className="text-2xl">{activeCol.emoji}</span>
                <div>
                  <h2 className="text-snow font-bold text-lg">{activeCol.name}</h2>
                  {activeCol.description && <p className="text-snow/40 text-xs">{activeCol.description}</p>}
                </div>
                <div className="ml-auto text-xs text-snow/30">
                  {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            {/* Items grid */}
            {filteredItems.length === 0 ? (
              items.length === 0 ? (
                <EmptyState onAdd={() => setShowAddItem(true)} />
              ) : (
                <div className="text-center py-16">
                  <div className="text-5xl mb-4 opacity-40">🔍</div>
                  <p className="text-snow/40 text-sm">No items match your filters</p>
                  <button onClick={() => { setSearchQuery(''); setFilterType('all'); }} className="mt-3 text-xs text-gold hover:underline">Clear filters</button>
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredItems.map(item => (
                  <ItemCard
                    key={item.id}
                    item={item}
                    selected={selectedIds.includes(item.id)}
                    onSelect={() => toggleSelect(item.id)}
                    onRemove={() => removeItem(item.id)}
                    compareMode={compareMode}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {/* Modals */}
      {(showColModal || editingCol) && (
        <CollectionModal
          initial={editingCol || undefined}
          onSave={editingCol ? updateCollection : createCollection}
          onClose={() => { setShowColModal(false); setEditingCol(null); }}
        />
      )}

      {showAddItem && (
        <AddItemModal
          collections={collections}
          onSave={addItem}
          onClose={() => setShowAddItem(false)}
        />
      )}

      {/* Compare panel */}
      {compareMode && selectedIds.length >= 2 && (
        <ComparePanel items={compareItems} onClose={() => setSelectedIds([])} />
      )}
    </main>
  );
}
